import { sendSignInLink, signInWithGoogle } from '../auth.js';

/**
 * LoginModal.js - Authentication UI component
 * Provides Google OAuth and email link sign-in options
 */

export class LoginModal {
  constructor() {
    this.email = '';
    this.showEmailForm = false;
    this.loading = false;
  }

  async handleGoogleSignIn() {
    this.loading = true;
    this.render();
    try {
      const user = await signInWithGoogle();
      if (user) {
        console.log('✅ Signed in with Google:', user.email);
        this.close();
        window.dispatchEvent(
          new CustomEvent('auth-success', { detail: { user, method: 'google' } })
        );
        return;
      }
    } catch (error) {
      console.error('❌ Google sign-in error:', error);
      alert('Sign-in failed: ' + error.message);
    }
    this.loading = false;
    this.render();
  }

  async handleEmailSignIn(e) {
    e.preventDefault();
    this.loading = true;
    this.render();
    try {
      await sendSignInLink(this.email);
      alert(
        `✅ Check your email (${this.email}) for the sign-in link!\n\nClick the link to sign in.`
      );
      this.email = '';
      this.showEmailForm = false;
    } catch (error) {
      console.error('❌ Email sign-in error:', error);
      alert('Error: ' + error.message);
    }
    this.loading = false;
    this.render();
  }

  toggleEmailForm() {
    this.showEmailForm = !this.showEmailForm;
    if (this.showEmailForm) {
      this.email = '';
    }
    this.render();
  }

  render() {
    const html = `
      <div class="login-modal">
        <div class="login-card">
          <h2>Sign in to Gentle Connections</h2>
          <p class="subtitle">Create a handbook for your child's teachers</p>

          <!-- Google Sign-In -->
          <button
            class="btn btn-primary btn-large"
            id="btn-google"
            ${this.loading ? 'disabled' : ''}
          >
            ${this.loading ? '⏳ Signing in...' : '🔐 Sign in with Google'}
          </button>

          <!-- Divider -->
          <div class="divider-line">
            <span>or</span>
          </div>

          <!-- Email Sign-In -->
          ${!this.showEmailForm
            ? `
            <button
              class="btn btn-secondary btn-large"
              id="btn-email-toggle"
              ${this.loading ? 'disabled' : ''}
            >
              📧 Sign in with Email
            </button>
          `
            : `
            <form id="email-form" class="email-form">
              <input
                type="email"
                id="email-input"
                placeholder="your.email@example.com"
                value="${this.email}"
                required
                ${this.loading ? 'disabled' : ''}
              />
              <div class="form-buttons">
                <button
                  type="submit"
                  class="btn btn-primary"
                  ${this.loading ? 'disabled' : ''}
                >
                  ${this.loading ? '⏳ Sending...' : 'Send Link'}
                </button>
                <button
                  type="button"
                  class="btn btn-outline"
                  id="btn-cancel-email"
                  ${this.loading ? 'disabled' : ''}
                >
                  Cancel
                </button>
              </div>
              <p class="form-hint">We'll send you a secure link to sign in. No password needed.</p>
            </form>
          `}

          <p class="auth-note">
            🔒 Your data is encrypted and secure. We never share your information.
          </p>
        </div>
      </div>
    `;

    // Replace modal content or insert if it doesn't exist
    let modal = document.getElementById('login-modal');
    if (!modal) {
      modal = document.createElement('div');
      modal.id = 'login-modal';
      modal.className = 'login-modal-wrapper';
      document.body.appendChild(modal);
    }
    modal.innerHTML = html;

    // Attach event listeners
    this.attachEventListeners();
  }

  attachEventListeners() {
    const btnGoogle = document.getElementById('btn-google');
    const btnEmailToggle = document.getElementById('btn-email-toggle');
    const emailForm = document.getElementById('email-form');
    const emailInput = document.getElementById('email-input');
    const btnCancelEmail = document.getElementById('btn-cancel-email');

    if (btnGoogle) {
      btnGoogle.addEventListener('click', () => this.handleGoogleSignIn());
    }

    if (btnEmailToggle) {
      btnEmailToggle.addEventListener('click', () => this.toggleEmailForm());
    }

    if (emailForm) {
      emailForm.addEventListener('submit', (e) => {
        if (emailInput) {
          this.email = emailInput.value;
        }
        this.handleEmailSignIn(e);
      });
    }

    if (emailInput) {
      emailInput.addEventListener('input', (e) => {
        this.email = e.target.value;
      });
    }

    if (btnCancelEmail) {
      btnCancelEmail.addEventListener('click', () => this.toggleEmailForm());
    }
  }

  // Close modal and clean up
  close() {
    const modal = document.getElementById('login-modal');
    if (modal) {
      modal.remove();
    }
  }
}

export function showLoginModal() {
  const existingModal = document.getElementById('login-modal');
  if (existingModal) {
    return { close: () => existingModal.remove() };
  }
  const modal = new LoginModal();
  modal.render();
  return modal;
}
